import { useState } from 'react'

import { Skeleton } from '@mui/material'
import type { AbortResetClient } from 'commons/customHooks'
import { convertBooleanApiValue } from 'commons/utils/string'
import { Checkbox, Flex, Info, Modal, type OptionProps, Select } from 'components'
import {
  QuotaLevel,
  ShareLevel,
  TypeRuleExceptionStock,
  TypesValidations,
  YesNoNotApplicable,
} from 'constants/enums'
import { t } from 'i18next'
import { Controller, useFormContext, useFormState } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { asyncGetMaterialById, type MaterialByIdResponse } from 'store/ProductContext'
import { setShowDetailedStock } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'

import * as S from './styles'

const FactoryStock = ({ resetController }: AbortResetClient) => {
  const { control } = useFormContext()
  const dispatch = useDispatch()
  const [factoryModal, setFactoryModal] = useState(false)
  const [selectedFactory, setSelectedFactory] = useState('')
  const { isDirty } = useFormState()

  const { orderNumber, materialNumber } = useParams()
  const { showDetailedStock } = useSelector((state) => state.structure)
  const { materialSelected, defaultParams } = useSelector((state) => state.products)
  const {
    draftBody: { fl_utlz_fair_shar, dt_dsjd_pedi },
  } = useSelector((state) => state.salesOrder)
  const { materialShares, loadingMaterialShare } = useSelector((state) => state.fairShare)
  const materialLevel = materialShares.colors.find((color) => color.level !== null)?.level

  const {
    qt_cota_disp_gnv,
    qt_cota_disp_grv,
    qt_cota_disp_zona,
    centros,
    fl_estq_lvre,
    fl_pivo_lvre,
    res_cent_matr,
    tp_estq_exce,
    tp_bloq_pivo,
  } = materialSelected

  const isOutOfLine = defaultParams.fl_matr_fora_linh === TypesValidations.YES

  const isFairShareOffice = convertBooleanApiValue(fl_utlz_fair_shar)

  const getFactoryOptions = () => {
    if (
      !centros.find((factory) => factory.cd_cent_matr === res_cent_matr) &&
      !!res_cent_matr.trim()
    ) {
      const factoryOptions: MaterialByIdResponse['centros'] = [
        ...centros,
        { cd_cent_matr: res_cent_matr },
      ]
      return setFactoryOptions(factoryOptions)
    }
    return setFactoryOptions(centros)
  }

  const setFactoryOptions = (centers: MaterialByIdResponse['centros']): OptionProps[] => {
    const newCentros = centers.map((factory) => ({
      title: factory.cd_cent_matr,
      value: factory.cd_cent_matr,
    }))
    const todos = {
      title: 'Todos',
      value: '',
    }
    if (materialSelected?.fl_pedi_antp === TypesValidations.YES) {
      newCentros.unshift(todos)
    }
    return newCentros
  }

  const handleFactoryChange = (value: string) => {
    const { controller } = resetController()

    dispatch(
      asyncGetMaterialById(
        {
          ...defaultParams,
          cd_cent_matr: value,
          cd_matr: materialNumber,
          dt_dsjd_pedi,
          nr_pedi_rasc: orderNumber,
        },
        '1',
        controller.signal,
      ),
    )
  }

  const getStockType = (tpEstqExce: MaterialByIdResponse['tp_estq_exce']) => {
    if (tpEstqExce.includes(TypeRuleExceptionStock.MATERIAL)) return 'Material'
    if (tpEstqExce.includes(TypeRuleExceptionStock.COR)) return 'Cor'
    if (tpEstqExce.includes(TypeRuleExceptionStock.SKU)) return 'SKU'
    if (tpEstqExce.includes(TypeRuleExceptionStock.CONTRAESTOQUE)) return 'Contra estoque'
    return 'SKU'
  }

  const renderFactorySelect = () => {
    return (
      <S.FactorySelect>
        <S.FactoryLabel>Centro</S.FactoryLabel>
        <S.FactorySelectWrapper>
          <Controller
            control={control}
            name="cd_cent_matr"
            render={({ field: { name, value, onChange } }) => (
              <Select
                defaultValue={getFactoryOptions()[0].value}
                disabled={!centros.length || !getFactoryOptions()[0].title.trim()}
                id="factory-centro"
                name={name}
                onChange={(e) => {
                  const valueCdCentMatr = e.target?.value

                  if (typeof valueCdCentMatr !== 'string') return

                  if (isDirty) {
                    setSelectedFactory(valueCdCentMatr)
                    setFactoryModal(true)
                  } else {
                    handleFactoryChange(valueCdCentMatr)
                    onChange(e)
                  }
                }}
                options={getFactoryOptions()}
                required={!!centros.length}
                value={value}
              />
            )}
          />
        </S.FactorySelectWrapper>
        <S.StockCheckbox>
          <Checkbox
            checked={showDetailedStock}
            label="Detalhar Estoque"
            onChange={(e) => {
              dispatch(setShowDetailedStock(e.target.checked))
            }}
            size="small"
          />
          <Info iconSize={15} placement="top-end">
            Ao ativar este campo, você poderá visualizar o total de materiais disponíveis.
          </Info>
        </S.StockCheckbox>
      </S.FactorySelect>
    )
  }

  return (
    <>
      {isOutOfLine ? (
        renderFactorySelect()
      ) : (
        <S.Container>
          <S.QuotaWrapper>
            <S.QuotaContainer>
              {materialSelected?.fl_pedi_antp !== TypesValidations.YES && (
                <>
                  {isFairShareOffice ? (
                    <Flex direction="column" gap="xs">
                      <S.QuotaTitle>{t('common.level')}</S.QuotaTitle>
                      {loadingMaterialShare ? (
                        <Skeleton animation="wave" height={22} width={170} />
                      ) : (
                        <Flex gap="sm">
                          <S.QuotaInfoContainer activated={materialLevel === ShareLevel.Zone}>
                            <S.LevelValue>{t('common.zone')}</S.LevelValue>
                          </S.QuotaInfoContainer>
                          <S.QuotaInfoContainer activated={materialLevel === ShareLevel.Regional}>
                            <S.LevelValue>{t('common.grv')}</S.LevelValue>
                          </S.QuotaInfoContainer>
                          <S.QuotaInfoContainer activated={materialLevel === ShareLevel.National}>
                            <S.LevelValue>{t('common.gnv')}</S.LevelValue>
                          </S.QuotaInfoContainer>
                          <S.QuotaInfoContainer activated={materialLevel === ShareLevel.BMU}>
                            <S.LevelValue>{t('common.bmu')}</S.LevelValue>
                          </S.QuotaInfoContainer>
                        </Flex>
                      )}
                    </Flex>
                  ) : (
                    <>
                      <S.QuotaInfoContainer activated={tp_bloq_pivo.includes(QuotaLevel.Zone)}>
                        <S.QuotaTitle>Cota Zona</S.QuotaTitle>
                        <S.QuotaValue>{qt_cota_disp_zona.toLocaleString()}</S.QuotaValue>
                      </S.QuotaInfoContainer>
                      <S.QuotaInfoContainer activated={tp_bloq_pivo.includes(QuotaLevel.Regional)}>
                        <S.QuotaTitle>Cota GRV</S.QuotaTitle>
                        <S.QuotaValue>{qt_cota_disp_grv.toLocaleString()}</S.QuotaValue>
                      </S.QuotaInfoContainer>
                      <S.QuotaInfoContainer activated={tp_bloq_pivo.includes(QuotaLevel.National)}>
                        <S.QuotaTitle>Cota GNV</S.QuotaTitle>
                        <S.QuotaValue>{qt_cota_disp_gnv.toLocaleString()}</S.QuotaValue>
                      </S.QuotaInfoContainer>
                      <S.QuotaInfoContainer>
                        <S.QuotaTitle>Cota Livre</S.QuotaTitle>
                        <S.QuotaValue>
                          {fl_pivo_lvre === YesNoNotApplicable.NotApplicable
                            ? YesNoNotApplicable.NO
                            : fl_pivo_lvre}
                        </S.QuotaValue>
                      </S.QuotaInfoContainer>
                      <S.QuotaInfoContainer>
                        <S.QuotaTitle>Estoque Livre</S.QuotaTitle>
                        <S.QuotaValue>
                          {fl_estq_lvre === YesNoNotApplicable.NotApplicable
                            ? YesNoNotApplicable.NO
                            : fl_estq_lvre}
                        </S.QuotaValue>
                      </S.QuotaInfoContainer>
                    </>
                  )}
                  <S.QuotaInfoContainer>
                    <S.QuotaTitle>Regra de Exceção do Estoque</S.QuotaTitle>
                    <S.QuotaValue>{getStockType(tp_estq_exce)}</S.QuotaValue>
                  </S.QuotaInfoContainer>
                </>
              )}
            </S.QuotaContainer>
            {renderFactorySelect()}
          </S.QuotaWrapper>
        </S.Container>
      )}
      <Modal
        buttonAction={() => handleFactoryChange(selectedFactory)}
        buttonText="Sim"
        description="Ao alterar a Fábrica/Centro, os dados digitados serão perdidos. Deseja continuar?"
        open={factoryModal}
        returnAction={() => setFactoryModal(false)}
        returnIcon={<></>}
        returnText="Não"
        setOpen={setFactoryModal}
        showReturn
        title="Alterar Fábrica/Centro"
      />
    </>
  )
}

export default FactoryStock
